// Everything that touches MapLibre (docs/ARCHITECTURE.md §2.5; port of web/map.js): sources and layers, pins drawn
// on demand, groups (the source's clustering) with faces looked up when idle, areas, your location, and the
// off-screen pointers. It subscribes to the feed, view, profile and clock stores directly and sends a source new
// data only when its features changed; none of this goes through React. React talks to it only through the view
// store (`move`), and it tells React where the map is (`camera`).
import type * as GeoJSON from "geojson";
import type * as maplibregl from "maplibre-gl";
import { loadEvents } from "../data/load";
import { SHARED } from "../shared/config";
import { RULES, type Box, type Level } from "../shared/rules";
import { clockStore } from "../state/clock";
import { emojiOf, eventById, feedStore, interestLevelOf } from "../state/feed";
import { filtered, soonestFallback } from "../state/filtered";
import { profileStore } from "../state/profile";
import { closeSheet, openSheet, selectedId, setCamera, viewStore, type MapMove, type ViewState } from "../state/view";
import { SelectedPin, popGroup } from "./selected";
import type { FeedEvent } from "../data/event";
import { cssColor, groupFace, nextLookChange, pinFeature, pinImageArgs, rankMember, TONES, type PinFeature } from "./draw";
import { paint, warmUp } from "./images";
import { timed } from "../perf";
import type { PinImage } from "./paint";
import { Hover } from "./hover";
import { CLUSTER_RADIUS, GROUP_LAYER, PIN_LAYERS } from "./layers";
import { Pointers } from "./pointers";
import { ZoomSlide } from "./slide";

type GeoJSONSource = maplibregl.GeoJSONSource;
const emptyFC = (): GeoJSON.FeatureCollection => ({ type: "FeatureCollection", features: [] });
// A group's members looked up for its face once the map is idle (at most this many).
const GROUP_LOOKUP = 400;
// Group images kept before the ones off screen are dropped (pruneGroupImages).
const GROUP_KEEP = 300;

export class MapController {
  ready = false;
  readonly pointers: Pointers;
  private unsub: (() => void)[] = [];
  private frame = 0;
  private lastKey: string | null = null;
  private lastView: unknown = null;
  private lookChangeAt = Infinity;
  /** Redraw the pins on the next render whatever the key says (a popping group starts or ends). */
  forceDraw = false;
  private sent = new Map<string, unknown>();
  private sentLists = new Map<string, PinFeature[]>();
  private groupVersion = 0;
  /** Group images on the map: the data version they were drawn for, their cluster, count, look and face. */
  private groupImages = new Map<string, { version: number; cluster: number; count: number; style: ReturnType<typeof RULES.pinFace>; face: string }>();
  private drawings = new Map<string, Promise<PinImage>>();

  constructor(readonly map: maplibregl.Map) {
    this.pointers = new Pointers(this);
    warmUp();
    map.on("load", () => this.onLoad());
  }

  destroy() {
    this.unsub.forEach((u) => u());
    cancelAnimationFrame(this.frame);
    this.pointers.destroy();
    this.hover?.destroy();
    this.slide?.destroy();
  }

  private onLoad() {
    const map = this.map, accent = cssColor("accent");
    // MapLibre 6: images are made on demand by a resolver (an event listener can no longer supply one in time).
    map.setMissingStyleImageResolver((id) => this.onMissingImage(id));
    map.on("idle", () => void this.refreshGroups());
    // Three numbers per group, whatever its size: its tone (green if any member is now, else blue if any is soon),
    // whether any member is free, and the best member's rank (its emoji until the others are looked up, its level).
    map.addSource("areas", { type: "geojson", data: emptyFC() });
    map.addSource("secondary", { type: "geojson", data: emptyFC() });
    map.addSource("single", { type: "geojson", data: emptyFC() });
    map.addSource("thin", { type: "geojson", data: emptyFC() });
    map.addSource("primary", { type: "geojson", data: emptyFC(), cluster: true, clusterRadius: CLUSTER_RADIUS, clusterMaxZoom: 16,
      clusterProperties: { tone: ["max", ["get", "tone"]], free: ["max", ["get", "free"]], best: ["min", ["get", "rank"]] } });
    map.addSource("me", { type: "geojson", data: emptyFC() });

    // Event areas: light shaded shapes under the pins.
    map.addLayer({ id: "area-fill", type: "fill", source: "areas", paint: { "fill-color": accent, "fill-opacity": ["case", ["get", "secondary"], 0.08, 0.15] } });
    map.addLayer({ id: "area-line", type: "line", source: "areas", paint: { "line-color": accent, "line-opacity": ["case", ["get", "secondary"], 0.25, 0.45], "line-width": 1.5 } });
    // Faded later search matches (and the selected / "soonest" pin outside the filters) stay single.
    map.addLayer({ id: "secondary-pins", type: "symbol", source: "secondary",
      layout: { "icon-image": ["get", "icon"], "icon-size": 0.8, "icon-allow-overlap": true, "symbol-sort-key": ["get", "sort"] },
      paint: { "icon-opacity": ["*", SHARED.pins.secondaryOpacity, ["get", "opacity"]] } });
    // Ticketed things that don't match an interest: never grouped, first to drop out when the map is crowded.
    map.addLayer({ id: "thin-pins", type: "symbol", source: "thin",
      layout: { "icon-image": ["get", "icon"], "icon-allow-overlap": false, "icon-padding": 1, "symbol-sort-key": ["get", "sort"] },
      paint: { "icon-opacity": ["get", "opacity"] } });
    map.addLayer({ id: "single-pins", type: "symbol", source: "single",
      layout: { "icon-image": ["get", "icon"], "icon-allow-overlap": true, "symbol-sort-key": ["get", "sort"] },
      paint: { "icon-opacity": ["get", "opacity"] } });
    map.addLayer({ id: GROUP_LAYER, type: "symbol", source: "primary", filter: ["has", "point_count"],
      layout: { "icon-image": ["concat", "group|", ["to-string", ["get", "cluster_id"]], "|", ["to-string", ["get", "point_count"]],
        "|", ["to-string", ["get", "tone"]], "|", ["to-string", ["get", "best"]], "|", ["to-string", ["get", "free"]]],
      "icon-allow-overlap": true }, paint: { "icon-opacity-transition": { duration: 0, delay: 0 } } });
    map.addLayer({ id: "primary-pins", type: "symbol", source: "primary", filter: ["!", ["has", "point_count"]],
      layout: { "icon-image": ["get", "icon"], "icon-allow-overlap": true, "symbol-sort-key": ["get", "sort"] },
      paint: { "icon-opacity": ["get", "opacity"], "icon-opacity-transition": { duration: 0, delay: 0 } } });
    map.addLayer({ id: "me", type: "circle", source: "me",
      paint: { "circle-radius": 7, "circle-color": accent, "circle-stroke-width": 3, "circle-stroke-color": cssColor("surface") } });

    for (const layer of [...PIN_LAYERS, GROUP_LAYER]) {
      map.on("mouseenter", layer, () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", layer, () => { map.getCanvas().style.cursor = ""; });
    }
    map.on("click", GROUP_LAYER, (ev) => {
      const f = ev.features?.[0];
      if (f) { ev.preventDefault(); void this.groupClick(f); }
    });
    // Tapping a pin centres the map on it and opens its card; it grows while selected (SelectedPin).
    for (const layer of PIN_LAYERS) {
      map.on("click", layer, (ev) => {
        const id = ev.features?.[0]?.properties?.id as string | undefined, e = id ? eventById(id) : null;
        if (!e || ev.defaultPrevented) return;
        ev.preventDefault();
        map.easeTo({ center: [e.lng, e.lat] });
        openSheet({ kind: "event", id: e.id });
      });
    }
    // Tapping empty map closes the card, like the phone.
    map.on("click", (ev) => { if (!ev.defaultPrevented && viewStore.getState().sheet?.kind === "event") closeSheet(); });
    // Moving the map yourself (not our own easeTo) deselects the pin: it shrinks back and its card closes.
    map.on("movestart", (ev) => { if ((ev as { originalEvent?: unknown }).originalEvent && viewStore.getState().sheet?.kind === "event") closeSheet(); });
    map.on("moveend", () => { this.publishCamera(); void loadEvents("area"); this.pointers.update(); });
    map.on("move", () => this.pointers.updateSoon());
    // Touching the map puts the keyboard away (phone), like tapping the map on iOS.
    const blur = () => (document.activeElement as HTMLElement | null)?.blur?.();
    map.on("mousedown", blur);
    map.on("touchstart", blur);

    this.hover = new Hover(this);
    this.slide = new ZoomSlide(this);
    this.ready = true;
    this.publishCamera();
    // The first fetch may have gone out before the map knew its size: make sure what's on screen is fetched.
    void loadEvents("area");
    const schedule = () => this.schedule();
    this.unsub.push(feedStore.subscribe(schedule), profileStore.subscribe(schedule), clockStore.subscribe(schedule),
      viewStore.subscribe((s, prev) => this.onView(s, prev)));
    // A move asked for before the map was ready (your location, a shared event): made now.
    const pending = viewStore.getState().move;
    if (pending) this.applyMove(pending);
    this.render();
  }

  private onView(s: ViewState, prev: ViewState) {
    if (s.move && s.move !== prev.move) this.applyMove(s.move);
    if (s.camera !== prev.camera && s.move === prev.move && s.filter === prev.filter && s.sheet === prev.sheet
      && s.userLocation === prev.userLocation) {
      // Only the camera changed (we set it): pins depend on it only through the "soonest" fallback.
      this.schedule();
      return;
    }
    if (s.nearbyExpanded !== prev.nearbyExpanded || s.sheet !== prev.sheet) setTimeout(() => this.pointers.update(), 300);
    this.schedule();
  }

  private applyMove({ center, zoom, atLeast }: MapMove) {
    this.map.easeTo({ center: [center.lng, center.lat], ...(zoom != null ? { zoom: atLeast ? Math.max(this.map.getZoom(), zoom) : zoom } : {}) });
  }

  private publishCamera() {
    const b = this.map.getBounds(), c = this.map.getCenter();
    const bounds: Box = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    setCamera({ bounds, center: { lat: c.lat, lng: c.lng }, zoom: this.map.getZoom() });
  }

  /** Coalesces store changes into one redraw per frame. */
  schedule() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; this.render(); });
  }

  private setSource(name: string, features: GeoJSON.Feature[], key: unknown) {
    if (this.sent.get(name) === key) return;
    this.sent.set(name, key);
    (this.map.getSource(name) as GeoJSONSource).setData({ type: "FeatureCollection", features });
    if (name === "primary") { this.groupVersion++; this.hover?.dataChanged(); }
  }

  render() {
    if (this.ready) timed("map render", () => this.renderNow());
  }
  private renderNow() {
    const fs = filtered(), now = clockStore.getState().now, view = profileStore.getState().view, vs = viewStore.getState();
    const fallback = soonestFallback();
    const selId = selectedId(vs), sel = selId ? eventById(selId) : null;
    // Everything the pins depend on besides the clock; the clock only matters once a pin's look is due to change.
    const key = [fs.version, fallback?.id, sel?.id, feedStore.getState().tagEmoji.size, this.extraKey()].join("|");
    if (key !== this.lastKey || view !== this.lastView || +now >= this.lookChangeAt || this.forceDraw) {
      this.forceDraw = false;
      this.lastKey = key; this.lastView = view;
      this.drawPins(fs.primary, fs.primaryIds, fs.secondary, fallback, sel, now);
    }
    const loc = vs.userLocation;
    this.setSource("me", loc ? [{ type: "Feature", geometry: { type: "Point", coordinates: [loc.lng, loc.lat] }, properties: {} }] : [],
      loc ? `${loc.lat},${loc.lng}` : "");
    this.afterRender(sel);
    this.pointers.update();
  }

  // The selected pin and a group's pins popping out are drawn as markers (selected.ts), not by the map's layers.
  private selected = new SelectedPin(this);
  private hover: Hover | null = null;
  /** Pins and groups sliding to their new spots after a zoom step (slide.ts). */
  slide: ZoomSlide | null = null;
  /** The slide draws the grouped pins itself while it runs: the map's copies hide (and show again in one frame). */
  slideHides(on: boolean) { this.hover?.hideForSlide(on); }
  /** A tapped group's pins while they pop out. */
  popping: { ids: Set<string>; key: number } | null = null;
  private extraKey() { return `${this.popping?.key ?? ""}`; }
  private drawnElsewhere(e: FeedEvent) { return e.id === selectedId() || !!this.popping?.ids.has(e.id); }
  private afterRender(sel: FeedEvent | null) { this.selected.update(sel); }

  private drawPins(prim: FeedEvent[], primIds: Set<string>, sec: FeedEvent[], fallback: FeedEvent | null, sel: FeedEvent | null, now: Date) {
    const primaryF: PinFeature[] = [], thinF: PinFeature[] = [], secondaryF: PinFeature[] = [], singleF: PinFeature[] = [];
    const interests = profileStore.getState().view.interests as Set<string>;
    let changeAt = Infinity;
    const place = (e: FeedEvent, isSecondary: boolean) => {
      changeAt = Math.min(changeAt, nextLookChange(e, now));
      if (this.drawnElsewhere(e)) return;
      const level: Level = interestLevelOf(e, interests);
      const thinsOut = RULES.openness(e) === "ticketed" && level !== "interest", passesFilters = primIds.has(e.id);
      const f = pinFeature(e, now, level, emojiOf(e));
      if (isSecondary) secondaryF.push(f);
      else if (thinsOut) thinF.push(f);
      else if (RULES.joinsGroup({ passesFilters, thinsOut })) primaryF.push(f);
      else singleF.push(f);
    };
    prim.forEach((e) => place(e, false));
    sec.forEach((e) => place(e, e.id !== fallback?.id));
    const shown = new Set([...primIds, ...sec.map((e) => e.id)]);
    if (fallback && !shown.has(fallback.id)) { place(fallback, false); shown.add(fallback.id); }
    if (sel && !shown.has(sel.id)) { place(sel, false); shown.add(sel.id); }
    this.lookChangeAt = changeAt;

    const seen = new Set<string>();
    const areas = [...prim, ...sec, ...(fallback ? [fallback] : []), ...(sel ? [sel] : [])]
      .filter((e) => (e.area?.coordinates?.[0]?.length ?? 0) >= 3 && !seen.has(e.id) && !!seen.add(e.id))
      .map((e): GeoJSON.Feature => ({ type: "Feature", geometry: e.area as GeoJSON.Polygon,
        properties: { secondary: !primIds.has(e.id) && e.id !== fallback?.id && e.id !== sel?.id } }));
    // A source is sent again only when its features changed (they're cached per event and look, so compare them).
    const sameAs = (name: string, list: PinFeature[]) => {
      const old = this.sentLists.get(name); this.sentLists.set(name, list);
      return !!old && old.length === list.length && old.every((f, i) => f === list[i]);
    };
    for (const [name, list] of [["primary", primaryF], ["thin", thinF], ["secondary", secondaryF], ["single", singleF]] as const) {
      if (!sameAs(name, list)) this.setSource(name, list, Symbol(name));
    }
    this.setSource("areas", areas, JSON.stringify(areas.map((a) => [(a.geometry as GeoJSON.Polygon).coordinates[0]![0], a.properties!.secondary])));
  }

  // ---------- images the map asks for: pins by their look, groups by cluster ----------
  // Painted in the image worker; the map waits for them while it lays out, without holding up its frames.
  private async onMissingImage(id: string) {
    if (id.startsWith("pin|")) {
      const img = await paint({ kind: "pin", ...pinImageArgs(id) });
      if (!this.map.hasImage(id)) this.map.addImage(id, img, { pixelRatio: 2 });
    } else if (id.startsWith("group|")) await this.groupImage(id);
  }

  // A group's image is drawn whole the first time the map asks for it: its members are read (at most GROUP_LOOKUP)
  // before it's painted, so it never shows one emoji first and the full face later (the map waits for the image; the
  // members come from the source's worker in a moment). Its best member's emoji is exact even when only some are read.
  private async groupImage(id: string) {
    const [, cluster, count, tone, best, free] = id.split("|");
    const style = RULES.pinFace(TONES[Number(tone)] ?? "plain", free === "1", true), version = this.groupVersion;
    const face = await this.faceOf(Number(cluster), Number(best));
    const img = await this.groupDrawing(face, Number(count), style);
    if (this.map.hasImage(id)) return;
    this.map.addImage(id, img, { pixelRatio: 2 });
    this.groupImages.set(id, { version, cluster: Number(cluster), count: Number(count), style, face: face.emojis.join("") });
  }
  private async faceOf(cluster: number, best: number) {
    const top = rankMember(best);
    try {
      const leaves = await (this.map.getSource("primary") as GeoJSONSource).getClusterLeaves(cluster, GROUP_LOOKUP, 0);
      const face = groupFace(leaves.map((l) => rankMember(Number(l.properties?.rank))));
      if (face.emojis[0] === top.emoji) return face;
      return { ...face, emojis: [top.emoji, ...face.emojis.filter((e) => e !== top.emoji)].slice(0, SHARED.group.maxEmojis) };
    } catch {
      return groupFace([top]); // the group went away with newer data: its best emoji stands in
    }
  }
  // Drawings by look (emojis, count, colour): many groups look alike, and drawing one is the slow part.
  private groupDrawing(face: ReturnType<typeof groupFace>, count: number, style: ReturnType<typeof RULES.pinFace>) {
    const key = `${face.emojis.join("")}|${count}|${style.outline}|${style.shape}|${face.level}`;
    let d = this.drawings.get(key);
    if (!d) {
      if (this.drawings.size > 400) this.drawings.clear();
      d = paint({ kind: "group", face, count, style });
      this.drawings.set(key, d);
    }
    return d;
  }
  // When the map settles after the pins were sent again (a time boundary, a refresh, a filter): group images drawn for
  // the old data that are still on screen keep showing (nothing flips back to one emoji) and are checked against their
  // members now, redrawn in place only if the face changed; the ones no longer on screen go.
  private async refreshGroups() {
    if (this.map.isMoving()) return; // "idle" can come between the steps of a gesture; wait for it to end
    this.pruneGroupImages();
    const stale = [...this.groupImages].filter(([, g]) => g.version !== this.groupVersion);
    if (!stale.length) return;
    const shown = this.groupsOnScreen(), version = this.groupVersion;
    let changed = false;
    for (const [id, g] of stale) {
      if (!shown.has(id)) { this.groupImages.delete(id); if (this.map.hasImage(id)) this.map.removeImage(id); continue; }
      g.version = version;
      const face = await this.faceOf(g.cluster, Number(id.split("|")[4]));
      if (face.emojis.join("") === g.face || version !== this.groupVersion || !this.map.hasImage(id)) continue;
      g.face = face.emojis.join("");
      this.map.updateImage(id, await this.groupDrawing(face, g.count, g.style));
      changed = true;
    }
    // MapLibre 6 marks an updated image but doesn't redraw for it: ask, or it shows only once the map moves.
    if (changed) this.map.triggerRepaint();
  }
  /** The image ids of the groups drawn on screen now. */
  private groupsOnScreen() {
    return new Set(this.map.queryRenderedFeatures({ layers: [GROUP_LAYER] }).map((f) => {
      const p = f.properties ?? {};
      return ["group", p.cluster_id, p.point_count, p.tone, p.best, p.free].join("|");
    }));
  }

  /** At 10× a pan and a zoom leave hundreds of group images behind (one per cluster and look, until the data changes):
   *  past GROUP_KEEP, the ones not on screen go (the map asks for one again if it comes back). */
  private pruneGroupImages() {
    if (this.groupImages.size <= GROUP_KEEP) return;
    const shown = this.groupsOnScreen();
    for (const id of this.groupImages.keys()) {
      if (shown.has(id)) continue;
      this.groupImages.delete(id);
      if (this.map.hasImage(id)) this.map.removeImage(id);
    }
  }

  /** Tapping a group centres the map on it and zooms in a step, and its pins pop out (or, when zooming can't pull
   *  them apart, lists them). */
  private async groupClick(f: maplibregl.MapGeoJSONFeature) {
    await popGroup(this, f);
  }
}
